import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialogModule} from '@angular/material/dialog';
import {TranslateModule} from '@ngx-translate/core';
import {DialogCloseButtonComponent} from '@q9elements/ui-kit/components';
import {DragulaModule, DragulaService, Group} from 'ng2-dragula';
import {
  Q9HtmlCleanupPipe,
  Q9HtmlDecoderPipe,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {NgForOf} from '@angular/common';

import {DraggableDialogDirective} from '../../shared/directives/draggable-dialog.directive';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {DRAGULA_BAG_PREFIXES, DragulaEvent} from '../../../shared/models/dragula.interface';
import {DragulaConfigurationService} from '../../../shared/service/dragula-configuration.service';
import {ActivityCommands} from '../../../commands/activity/activity.commands';
import {UndoService} from '../../../commands/undo.service';
import {EditorRegistryService} from '../../../diagram-render/mx-graph/editor/editor-registry.service';
import {CellsHighlighter} from '../../../diagram-render/helpers/cells-highlighter';

@Component({
  selector: 'app-activity-reorder-dialog',
  templateUrl: './activity-reorder-dialog.component.html',
  styleUrls: ['./activity-reorder-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    DraggableDialogDirective,
    TranslateModule,
    DialogCloseButtonComponent,
    DragulaModule,
    NgForOf,
    Q9HtmlCleanupPipe,
    Q9HtmlDecoderPipe
  ]
})
export class ActivityReorderDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  @ViewChild('dragContainer', {read: ElementRef, static: true})
  public dragContainerRef: ElementRef<HTMLDivElement>;
  public list: any[] = [];
  public listMap: Map<number, any> = new Map();

  public bagPrefix = DRAGULA_BAG_PREFIXES.ACTIVITIES;
  private dragOpts = {direction: 'vertical', moves: () => true, copy: false};
  private bagGroup!: Group;

  private graph: any;
  private cellsHighlighter: CellsHighlighter;

  constructor(
    private readonly diagramJsonSvc: DiagramJsonHandlerService,
    private readonly dragulaConfigurationService: DragulaConfigurationService,
    private readonly dragulaService: DragulaService,
    private readonly activityCommands: ActivityCommands,
    private readonly undoSvc: UndoService,
    private readonly registry: EditorRegistryService
  ) {
    super();
  }

  ngOnInit() {
    this.diagramJsonSvc.diagramJson$.pipe(this.takeUntilComponentDestroy()).subscribe(json => {
      this.list = json.activities
        .map(({mxKey, text, number, strippedText}: any) => ({mxKey, text, number, strippedText}))
        .sort((a, b) => Number(a.number) - Number(b.number));

      this.listMap.clear();

      this.list.forEach((item: any, index: number) => {
        item.index = index;
        this.listMap.set(Number(item.mxKey), item);
      });
    });

    this.initDragula();

    this.graph = this.registry.getGraph();
    this.cellsHighlighter = new CellsHighlighter(this.graph, {
      hlColor: 'rgba(16, 193, 49, 0.8)',
      hlWidth: 4
    });
  }

  ngOnDestroy() {
    if (this.bagGroup && this.dragContainerRef) {
      const drake: any = this.bagGroup?.drake || {};
      const containers: Element[] = drake.containers || [];
      const index = containers.indexOf(this.dragContainerRef.nativeElement);

      if (index > -1) {
        containers.splice(index, 1);
      }
    }
  }

  public highlight(mxKey: number, enable: boolean) {
    this.cellsHighlighter.clearAll();
    const cell = this.graph.model.getCell(Number(mxKey));

    cell.comp.instance.toggleHighlight(enable);
  }

  private initDragula(): void {
    this.bagGroup = this.dragulaConfigurationService.createGroup({
      groupName: this.bagPrefix,
      ...this.dragOpts
    });
    this.dragulaService
      .dropModel(this.bagPrefix)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(data => this.onReorder(data));
  }

  private onReorder({el, target}: DragulaEvent<'dropModel'>) {
    if (el instanceof HTMLElement) {
      const newOrder = Array.from(target.children || []).map(child => ({
        mxKey: child['dataset']?.mxkey
      }));
      const differences = [];

      newOrder.forEach((item, index) => {
        const oldIndex = this.listMap.get(Number(item.mxKey)).index;

        if (oldIndex !== index) {
          differences.push({mxKey: Number(item.mxKey), number: index + 1, oldNumber: oldIndex + 1});
        }
      });

      const command = this.activityCommands.renumber(differences);

      this.undoSvc.executeCommand(command);
    }
  }
}
